import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { SUPERVISOR_STORE_EVALUATION_PAGE, SUPERVISOR_HOME_PAGE } from "../routes";
import { listEvaluationFormsApi, EvaluationFormListItem } from "../supervisorApi";
import {
  getStoreEvaluationByIdApi,
  openStoreEvaluationApi,
  setEntryPhotoApi,
  submitStoreEvaluationApi,
  resetStoreEvaluationApi,
  StoreEvaluation,
  StoreEvaluationAnswer,
} from "../../store-evaluations/storeEvaluationsApi";
import { useStoreEvaluationDraft } from "../hooks/useStoreEvaluationDraft";

import AuthImage from "../../../components/common/AuthImage";
import CaptureImage from "../../../components/common/CaptureImage";
import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";
import { getDevicePosition } from "../../../utils/geolocation";
import QuestionField, { hasAnswer, QuestionFieldValue } from "../components/QuestionField";

import "../components/SupFlow.css";

type StoreListItem = {
  id: string;
  nomeFantasia: string;
  endereco?: string | null;
  estado?: string | null;
};

type EvaluationQuestionType = "BOOLEAN" | "SCALE" | "TEXT" | "DATE" | "SINGLE_CHOICE" | "MULTI_CHOICE";

/** Vem do "Avaliar loja" (loja e dia) ou de "Auditorias em andamento" (id da aberta). */
type EvaluationPageLocationState = {
  selectedStore?: StoreListItem;
  referenceDate?: string;
  evaluationId?: string;
};

type EvaluationOption = {
  id: string;
  label: string;
  value?: string | null;
  order: number;
};

type EvaluationQuestion = {
  id: string;
  type: EvaluationQuestionType;
  prompt: string;
  required: boolean;
  order: number;
  minValue?: number | null;
  maxValue?: number | null;
  options?: EvaluationOption[];
};

type EvaluationFormWithQuestions = EvaluationFormListItem & {
  formType?: { name: string };
  questions: EvaluationQuestion[];
};

type AnswersState = Record<string, QuestionFieldValue>;

const STEPS = ["Dados", "Entrada", "Perguntas", "Saída"];

const shortBr = (ymd: string) => {
  const [, m, d] = ymd.split("-");
  return d && m ? `${d}/${m}` : ymd;
};

const time = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : null;

const initialAnswersFor = (f: EvaluationFormWithQuestions): AnswersState =>
  (f.questions ?? []).reduce<AnswersState>((acc, question) => {
    acc[question.id] = question.type === "MULTI_CHOICE" ? [] : null;
    return acc;
  }, {});

/** Resposta gravada como valor do `QuestionField` (o inverso do `buildAnswerPayload`). */
function valueFromAnswer(type: EvaluationQuestionType, a: StoreEvaluationAnswer): QuestionFieldValue {
  if (type === "BOOLEAN") return a.booleanValue ?? null;
  if (type === "SCALE") return a.intValue ?? null;
  if (type === "TEXT") return a.textValue ?? null;
  if (type === "DATE") return a.dateValue ? a.dateValue.slice(0, 10) : null;
  if (type === "SINGLE_CHOICE") return a.optionId ?? null;
  if (type === "MULTI_CHOICE") return Array.isArray(a.optionIds) ? a.optionIds : [];
  return null;
}

/** Respostas do formulário com o que a avaliação aberta já tem gravado. */
function answersFromEvaluation(f: EvaluationFormWithQuestions, ev: StoreEvaluation): AnswersState {
  const answers = initialAnswersFor(f);
  const typeById = new Map((f.questions ?? []).map((q) => [q.id, q.type]));
  for (const a of ev.answers ?? []) {
    const type = typeById.get(a.questionId);
    if (type) answers[a.questionId] = valueFromAnswer(type, a);
  }
  return answers;
}

const DRAFT_LABEL = {
  saving: "Salvando…",
  offline: "Sem conexão: salvo neste aparelho",
} as const;

/**
 * Auditoria de loja do supervisor (redesign de 15/10/2026): dados e formulário,
 * foto de entrada, perguntas e foto de saída. Avaliação enviada abre o detalhe
 * (`StoreEvaluationDetailPage`, na rota `/supervisor-store-evaluation/:id`).
 */
export const SupervisorStoreEvaluationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = useGoBack(SUPERVISOR_HOME_PAGE());
  const token = useSelector((s: RootState) => s.auth.accessToken);
  const userType = useSelector((s: RootState) => s.auth.userType);
  const navigationState = location.state as EvaluationPageLocationState | null;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [evaluation, setEvaluation] = useState<any | null>(null);
  const [selectedStore, setSelectedStore] = useState<StoreListItem | null>(null);
  const [referenceDate, setReferenceDate] = useState<string>("");

  const [form, setForm] = useState<EvaluationFormWithQuestions | null>(null);
  /** Formulários de loja que o perfil pode responder. Com mais de um, o usuário escolhe. */
  const [availableForms, setAvailableForms] = useState<EvaluationFormWithQuestions[]>([]);
  const [answers, setAnswers] = useState<AnswersState>({});
  const [missing, setMissing] = useState<Set<string>>(new Set());

  const [step, setStep] = useState<number>(0);
  const [exitPhotoAssetId, setExitPhotoAssetId] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const questions = useMemo(
    () => [...(form?.questions ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [form],
  );

  const buildDraftAnswers = useCallback(
    (ids: string[]) =>
      questions.filter((q) => ids.includes(q.id)).map((q) => buildAnswerPayload(q, answers[q.id] ?? null)),
    [questions, answers],
  );
  const draft = useStoreEvaluationDraft({ token, answers, exitPhotoAssetId, buildAnswers: buildDraftAnswers });
  const restoreDraft = draft.restore;

  /** Avaliação aberta na tela: respostas e foto de saída do rascunho, e a etapa onde parou. */
  const applyEvaluation = useCallback(
    (ev: StoreEvaluation, f: EvaluationFormWithQuestions) => {
      const restored = restoreDraft(ev.id, {
        answers: answersFromEvaluation(f, ev),
        exitPhotoAssetId: ev.exitPhotoAssetId ?? null,
        updatedAt: ev.updatedAt,
      });
      setEvaluation(ev);
      setAnswers(restored.answers);
      setExitPhotoAssetId(restored.exitPhotoAssetId);
      setMissing(new Set());
      setStep(!ev.entryPhotoAssetId ? 1 : restored.exitPhotoAssetId ? 3 : 2);
    },
    [restoreDraft],
  );

  const loadPageData = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      if (navigationState?.selectedStore) setSelectedStore(navigationState.selectedStore);
      if (navigationState?.referenceDate) setReferenceDate(navigationState.referenceDate);

      const formsList = await listEvaluationFormsApi({ token, category: "LOJA", includeQuestions: true });

      // Retomada pela home: a auditoria já tem loja, dia e formulário.
      if (navigationState?.evaluationId) {
        const ev = await getStoreEvaluationByIdApi({ token, evaluationId: navigationState.evaluationId });
        if (ev.status === "SUBMITTED") {
          navigate(SUPERVISOR_STORE_EVALUATION_PAGE(ev.id), { replace: true });
          return;
        }
        const evForm = ((formsList ?? []) as EvaluationFormWithQuestions[]).find((f) => f.id === ev.formId);
        if (!evForm) {
          toast.error("O formulário desta auditoria não está mais disponível.");
          navigate(SUPERVISOR_HOME_PAGE(), { replace: true });
          return;
        }
        if (ev.store) setSelectedStore(ev.store);
        setReferenceDate(ev.referenceDate.slice(0, 10));
        setAvailableForms([evForm]);
        setForm(evForm);
        applyEvaluation(ev, evForm);
        return;
      }

      // Lista vazia em allowedAnswererTypes = sem restrição; ADMIN_RESTRITO responde como ADMIN
      // (mesma regra do backend). Filtrado aqui porque o filtro da API exclui os sem restrição.
      const answererType = userType === "ADMIN_RESTRITO" ? "ADMIN" : userType;
      const eligible = ((formsList ?? []) as EvaluationFormWithQuestions[]).filter(
        (f) =>
          !f.allowedAnswererTypes?.length || (answererType ? f.allowedAnswererTypes.includes(answererType) : false),
      );
      setAvailableForms(eligible);

      if (eligible.length === 1) {
        setForm(eligible[0]);
        setAnswers(initialAnswersFor(eligible[0]));
      } else {
        setForm(null);
        setAnswers({});
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao carregar dados da avaliação.");
    } finally {
      setLoading(false);
    }
  }, [token, navigationState, userType, navigate, applyEvaluation]);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }
    if (!navigationState?.evaluationId && (!navigationState?.selectedStore || !navigationState?.referenceDate)) {
      toast.error("Selecione a loja e a data antes de iniciar a avaliação.");
      navigate(SUPERVISOR_HOME_PAGE(), { replace: true });
      return;
    }
    loadPageData();
  }, [token, navigate, loadPageData, navigationState]);

  const answeredCount = questions.filter((q) => hasAnswer(q.type, answers[q.id] ?? null)).length;

  const selectForm = (f: EvaluationFormWithQuestions) => {
    setForm(f);
    setAnswers(initialAnswersFor(f));
    setMissing(new Set());
  };

  const updateAnswer = (questionId: string, value: QuestionFieldValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setMissing((prev) => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  };

  const handleStartAudit = async () => {
    if (!token || !selectedStore || !form) return;
    if (!referenceDate) {
      toast.error("Selecione o dia da auditoria.");
      return;
    }

    try {
      setLoading(true);
      const evalData = await openStoreEvaluationApi({
        token,
        storeId: selectedStore.id,
        formId: form.id,
        referenceDate,
      });
      // Auditoria já aberta continua de onde parou, com o rascunho.
      applyEvaluation(evalData, form);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao iniciar auditoria.");
    } finally {
      setLoading(false);
    }
  };

  const handleEntryPhotoSuccess = async (assetId: string) => {
    if (!token || !evaluation) return;
    try {
      setLoading(true);
      // A posição vai junto: com o raio ligado, longe da loja o backend recusa com a distância.
      const position = await getDevicePosition();
      const updatedEval = await setEntryPhotoApi({ token, evaluationId: evaluation.id, assetId, position });
      setEvaluation(updatedEval);
      setStep(2);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao salvar foto de entrada.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetAudit = async () => {
    if (!token || !evaluation) return;
    try {
      setLoading(true);
      const updatedEval = await resetStoreEvaluationApi({ token, evaluationId: evaluation.id });
      setEvaluation(updatedEval);
      setConfirmReset(false);
      setStep(1);
      toast.info("Auditoria reiniciada. Envie a foto de entrada novamente.");
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao reiniciar auditoria.");
    } finally {
      setLoading(false);
    }
  };

  /** Destaca as obrigatórias sem resposta; `true` quando não falta nada. */
  const checkRequired = () => {
    const pending = questions.filter((q) => q.required && !hasAnswer(q.type, answers[q.id] ?? null));
    if (!pending.length) return true;
    setMissing(new Set(pending.map((q) => q.id)));
    toast.error(
      pending.length === 1
        ? `Preencha a pergunta obrigatória: ${pending[0].prompt}`
        : `Faltam ${pending.length} perguntas obrigatórias.`,
    );
    return false;
  };

  const handleAdvanceToExitPhoto = () => {
    if (!checkRequired()) return;
    void draft.flush();
    setStep(3);
  };

  const handleSubmitEvaluation = async () => {
    if (!token || !evaluation || !form) return;
    if (!exitPhotoAssetId) {
      toast.error("A foto de saída é obrigatória.");
      return;
    }
    // Retomada direto na Saída: as obrigatórias ainda não foram conferidas.
    if (!checkRequired()) {
      setStep(2);
      return;
    }

    try {
      setSubmitting(true);
      // O rascunho pendente vai antes, para não chegar depois do envio.
      await draft.flush();
      const payloadAnswers = questions.map((question) => buildAnswerPayload(question, answers[question.id] ?? null));
      const finalEval = await submitStoreEvaluationApi({
        token,
        evaluationId: evaluation.id,
        exitPhotoAssetId,
        answers: payloadAnswers,
        position: await getDevicePosition(),
      });
      draft.clear();
      toast.success("Avaliação finalizada com sucesso!");
      navigate(SUPERVISOR_STORE_EVALUATION_PAGE(finalEval.id), { replace: true });
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao enviar avaliação.");
    } finally {
      setSubmitting(false);
    }
  };

  // Voltar da faixa: passo anterior; nas perguntas, sai da tela (a auditoria fica aberta e retoma).
  const handleBack = () => {
    if (step === 1) setStep(0);
    else if (step === 3) setStep(2);
    else goBack();
  };

  const busy = loading || submitting;
  const draftLabel =
    draft.status === "saving" || draft.status === "offline"
      ? DRAFT_LABEL[draft.status]
      : draft.savedAt && draft.status === "saved"
        ? `Rascunho salvo às ${time(draft.savedAt.toISOString())}`
        : null;
  const storeName = selectedStore?.nomeFantasia ?? "Loja";
  const address = [selectedStore?.endereco, selectedStore?.estado].filter(Boolean).join(" · ");

  return (
    <div className="supFlow">
      <header className="supFlow__band">
        <div className="supFlow__bandInner">
          <div className="supFlow__bandTop">
            <button type="button" className="supFlow__back" onClick={handleBack} aria-label="Voltar" disabled={busy}>
              <EcoIcon name="arrowLeft" size={22} />
            </button>
            <span className="supFlow__topTitle">
              {step === 0 ? "Auditoria de loja" : `${storeName} · ${shortBr(referenceDate)}`}
            </span>
            <span className="supFlow__spacer" aria-hidden="true" />
          </div>

          {step === 0 ? (
            <div className="supFlow__heading">
              <h1 className="supFlow__title">{storeName}</h1>
              <span className="supFlow__subtitle">{address || "Endereço não informado"}</span>
            </div>
          ) : null}

          <ol className="supFlow__steps" aria-label="Etapas">
            {STEPS.map((label, i) => (
              <li
                key={label}
                className={`supFlow__step${i < step ? " supFlow__step--done" : i === step ? " supFlow__step--current" : ""}`}
                aria-current={i === step ? "step" : undefined}
              >
                {label}
              </li>
            ))}
          </ol>

          {step === 2 && questions.length ? (
            <div className="supFlow__progress">
              <span className="supFlow__progressBar" aria-hidden="true">
                <span
                  className="supFlow__progressFill"
                  style={{ width: `${(answeredCount / questions.length) * 100}%` }}
                />
              </span>
              <span>
                {answeredCount} de {questions.length} respondidas
              </span>
            </div>
          ) : null}

          {step > 0 && draftLabel ? (
            <span
              className={`supFlow__draft${draft.status === "offline" ? " supFlow__draft--offline" : ""}`}
              role="status"
            >
              {draftLabel}
            </span>
          ) : null}
        </div>
      </header>

      <main className="supFlow__main">
        {loading && !evaluation && step === 0 ? (
          <div className="supFlow__empty">Carregando dados...</div>
        ) : null}

        {/* Passo 1: dados e formulário */}
        {step === 0 && !loading ? (
          availableForms.length === 0 ? (
            <div className="supFlow__empty">
              Nenhum formulário de loja disponível para o seu perfil. Peça ao administrador para liberar um formulário.
            </div>
          ) : (
            <>
              <div className="supFlow__field">
                <label htmlFor="audit-date" className="supFlow__label">
                  Dia da auditoria
                </label>
                <input
                  id="audit-date"
                  type="date"
                  className="supFlow__date"
                  value={referenceDate}
                  onChange={(e) => setReferenceDate(e.target.value)}
                  disabled={busy}
                />
              </div>

              <fieldset className="supFlow__field">
                <legend className="supFlow__label">Formulário</legend>
                {availableForms.map((f) => {
                  const selected = form?.id === f.id;
                  return (
                    <label key={f.id} className={`supFlow__option${selected ? " supFlow__option--selected" : ""}`}>
                      <input
                        type="radio"
                        name="auditForm"
                        checked={selected}
                        onChange={() => selectForm(f)}
                        disabled={busy}
                      />
                      <span className="supFlow__optionText">
                        <span className="supFlow__optionTitle">{f.title}</span>
                        <span className="supFlow__optionSub">{f.questions?.length ?? 0} perguntas</span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              <div className="supFlow__info">
                <EcoIcon name="helpCircle" size={18} />
                <span>
                  Se já existe uma auditoria aberta desta loja, com este formulário e neste dia, ela continua de onde
                  parou.
                </span>
              </div>
            </>
          )
        ) : null}

        {/* Passo 2: foto de entrada */}
        {step === 1 ? (
          <>
            <div className="supFlow__intro">
              <h2 className="supFlow__introTitle">Foto de entrada</h2>
              <p className="supFlow__introText">
                Fotografe a fachada ou a entrada da loja ao chegar. A foto é tirada na hora, sem galeria.
              </p>
            </div>
            <CaptureImage
              token={token ?? ""}
              label=""
              confirmLabel="Usar e continuar"
              onUploadSuccess={handleEntryPhotoSuccess}
            />
          </>
        ) : null}

        {/* Passo 3: perguntas */}
        {step === 2 ? (
          <>
            {questions.map((q, i) => (
              <QuestionField
                key={q.id}
                index={i + 1}
                question={q}
                value={answers[q.id] ?? null}
                onChange={(v) => updateAnswer(q.id, v)}
                missing={missing.has(q.id)}
                disabled={busy}
              />
            ))}
            <button type="button" className="supFlow__danger" onClick={() => setConfirmReset(true)} disabled={busy}>
              Recomeçar auditoria (apaga só a foto de entrada)
            </button>
          </>
        ) : null}

        {/* Passo 4: foto de saída e envio */}
        {step === 3 ? (
          <>
            <div className="supFlow__intro">
              <h2 className="supFlow__introTitle">Foto de saída</h2>
              <p className="supFlow__introText">Fotografe a fachada ao sair. Depois é só enviar a auditoria.</p>
            </div>
            {exitPhotoAssetId ? (
              // Foto já salva no rascunho (inclusive ao retomar): mostra e deixa trocar.
              <div className="supFlow__summaryRow">
                <span className="supFlow__summaryIcon" style={{ overflow: "hidden", padding: 0 }}>
                  <AuthImage
                    token={token ?? ""}
                    assetId={exitPhotoAssetId}
                    alt="Foto de saída"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    statusClassName="supFlow__optionSub"
                  />
                </span>
                <span className="supFlow__optionText">
                  <span className="supFlow__optionTitle">Foto de saída</span>
                  <span className="supFlow__optionSub">Pronta para enviar</span>
                </span>
                <button
                  type="button"
                  className="ecoBtn ecoBtn--sm"
                  onClick={() => setExitPhotoAssetId(null)}
                  disabled={busy}
                >
                  Tirar outra
                </button>
              </div>
            ) : (
              <CaptureImage
                token={token ?? ""}
                label=""
                confirmLabel="Usar esta foto"
                onUploadSuccess={(assetId) => setExitPhotoAssetId(assetId)}
              />
            )}

            <section className="supFlow__summary" aria-label="Antes de enviar">
              <span className="supFlow__label">Antes de enviar</span>
              {evaluation?.entryPhotoAssetId ? (
                <div className="supFlow__summaryRow">
                  <span className="supFlow__summaryIcon" style={{ overflow: "hidden", padding: 0 }}>
                    <AuthImage
                      token={token ?? ""}
                      assetId={evaluation.entryPhotoAssetId}
                      alt="Foto de entrada"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      statusClassName="supFlow__optionSub"
                    />
                  </span>
                  <span className="supFlow__optionText">
                    <span className="supFlow__optionTitle">Foto de entrada</span>
                    <span className="supFlow__optionSub">
                      {time(evaluation.entryPhotoAt) ? `Tirada às ${time(evaluation.entryPhotoAt)}` : "Enviada"}
                    </span>
                  </span>
                  <EcoIcon name="check" size={20} strokeWidth={2.5} />
                </div>
              ) : null}
              <div className="supFlow__summaryRow">
                <span className="supFlow__summaryIcon">{answeredCount}</span>
                <span className="supFlow__optionText">
                  <span className="supFlow__optionTitle">Perguntas respondidas</span>
                  <span className="supFlow__optionSub">
                    {answeredCount} de {questions.length} · a nota sai depois do envio
                  </span>
                </span>
              </div>
            </section>
          </>
        ) : null}
      </main>

      {step === 0 && availableForms.length > 0 ? (
        <footer className="supFlow__footer">
          <div className="supFlow__footerInner">
            <button
              type="button"
              className="ecoBtn ecoBtn--primary"
              onClick={handleStartAudit}
              disabled={!form || !referenceDate || busy}
            >
              {loading ? "Abrindo..." : "Iniciar auditoria"}
              <EcoIcon name="arrowRight" size={18} />
            </button>
            {!form ? <span className="supFlow__footerHint">Escolha o formulário.</span> : null}
          </div>
        </footer>
      ) : null}

      {step === 2 ? (
        <footer className="supFlow__footer">
          <div className="supFlow__footerInner">
            <button type="button" className="ecoBtn ecoBtn--primary" onClick={handleAdvanceToExitPhoto} disabled={busy}>
              Ir para a foto de saída
              <EcoIcon name="arrowRight" size={18} />
            </button>
          </div>
        </footer>
      ) : null}

      {step === 3 ? (
        <footer className="supFlow__footer">
          <div className="supFlow__footerInner">
            <button
              type="button"
              className="ecoBtn ecoBtn--primary"
              onClick={handleSubmitEvaluation}
              disabled={submitting || !exitPhotoAssetId}
            >
              {submitting ? "Enviando..." : "Enviar auditoria"}
            </button>
            {!exitPhotoAssetId ? (
              <span className="supFlow__footerHint">Tire e use a foto de saída para liberar o envio.</span>
            ) : null}
          </div>
        </footer>
      ) : null}

      <EcoConfirmDialog
        open={confirmReset}
        title="Recomeçar a auditoria?"
        message="A foto de entrada é apagada e você volta para tirá-la de novo. As respostas já dadas continuam salvas."
        confirmLabel="Recomeçar"
        tone="danger"
        busy={loading}
        onConfirm={handleResetAudit}
        onCancel={() => setConfirmReset(false)}
      />
    </div>
  );
};

function buildAnswerPayload(question: EvaluationQuestion, value: QuestionFieldValue) {
  if (question.type === "BOOLEAN") {
    return { questionId: question.id, booleanValue: value === null ? null : Boolean(value) };
  }
  if (question.type === "SCALE") {
    return { questionId: question.id, intValue: typeof value === "number" ? value : null };
  }
  if (question.type === "TEXT") {
    return { questionId: question.id, textValue: typeof value === "string" ? value : null };
  }
  if (question.type === "DATE") {
    return { questionId: question.id, dateValue: typeof value === "string" && value ? value : null };
  }
  if (question.type === "SINGLE_CHOICE") {
    return { questionId: question.id, optionId: typeof value === "string" && value ? value : null };
  }
  if (question.type === "MULTI_CHOICE") {
    return { questionId: question.id, optionIds: Array.isArray(value) ? value : [] };
  }
  return { questionId: question.id };
}
